import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import dayjs, { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { addDays, endOfWeek, startOfWeek } from 'date-fns';

import './team-trainings.page.css';
import CreateTrainingDraftComponent from '../../../../components/training-sideviews/create-training-draft/create-training-draft.component';
import COETrainingLibraryComponent from '../../../../components/training-sideviews/coe-training-library/coe-training-library.component';
import COEWeeklyPlannersComponent from '../../../../components/weekly-training-planner/coe-weekly-planners/coe-weekly-planners.component';
import EditTrainingComponent from '../../../../components/training-sideviews/edit-training/edit-training.component';
import TpWeekCalendar from '../../../../components/team-training-plan/tp-week-calendar.component';
import TpGroupMembersDialog from '../../../../components/team-training-plan/tp-group-members.dialog';
import TpCreateGroupDialog from '../../../../components/team-training-plan/tp-create-group.dialog';
import TpFieldsFormsPanel from '../../../../components/team-training-plan/tp-fields-forms.panel';
import COESkeletonComponent from '../../../../components/general/coe-skeleton/coe-skeleton.component';
import { Group, Team } from '../../../../models/training.models';
import useService from '../../../../hooks/service.hook';
import TrainingsService from '../../../../services/trainings.service';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import { formatDuration } from '../../../../helpers/general.helpers';
import { getDefaultWeek } from '../../../../helpers/datetime.helpers';
import {
  SplitWeekStats,
  WeekSchedule,
  buildWeekScheduleFromTrainings,
  calculateSplitWeekStats,
  createEmptyWeekSchedule,
  getWeekDateRangeLabel,
} from '../../../../helpers/team-training-plan.helpers';
import { palette } from '../../../../theme';

import '../../../../components/team-training-plan/tp-week-calendar.component.css';

interface TeamTrainingsPageProps {
  team: Team;
  refreshTeam: () => void;
}

type SubTab = 'trainingPlan' | 'fieldsForms';
type ModalContext = { type: 'team' | 'group'; groupId?: string };

interface GroupWeekData {
  groupId: string;
  trainings: any[];
  weekSchedule: WeekSchedule;
  stats: SplitWeekStats;
  loading: boolean;
}

const roundToNearestMinutes = (date: Date, minutes: number): Date => {
  const ms = 1000 * 60 * minutes;
  return new Date(Math.round(date.getTime() / ms) * ms);
};

const normalizeTrainings = (trainings: any[]): any[] =>
  trainings.map((training) => ({
    ...training,
    sport: `${training.sport}/${training.sportType}/${training.focus}`,
    totalDurationPlanned: formatDuration(training.duration),
  }));

const TeamTrainingsPage: FC<TeamTrainingsPageProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackbar } = useSnackbarHelper();
  const trainingsService = useService(TrainingsService);

  const initialSub = (searchParams.get('sub') as SubTab) || 'trainingPlan';
  const [subTab, setSubTab] = useState<SubTab>(initialSub === 'fieldsForms' ? 'fieldsForms' : 'trainingPlan');

  const [week, setWeek] = useState<Dayjs>(
    dayjs(searchParams.get('week')).isValid() ? dayjs(searchParams.get('week')) : getDefaultWeek()
  );

  const [modalState, setModalState] = useState('');
  const [currentTraining, setCurrentTraining] = useState<any>(null);
  const [draftTrainingDate, setDraftTrainingDate] = useState(roundToNearestMinutes(new Date(), 5));
  const [trainingLibraryOpen, setTrainingLibraryOpen] = useState(false);
  const [weeklyPlannerOpen, setWeeklyPlannerOpen] = useState(false);
  const [modalContext, setModalContext] = useState<ModalContext>({ type: 'team' });

  const [teamTrainings, setTeamTrainings] = useState<any[]>([]);
  const [teamWeekArr, setTeamWeekArr] = useState<WeekSchedule>(createEmptyWeekSchedule(week));
  const [teamStats, setTeamStats] = useState<SplitWeekStats>(calculateSplitWeekStats([]));
  const [teamLoading, setTeamLoading] = useState(true);

  const [groupWeeks, setGroupWeeks] = useState<Record<string, GroupWeekData>>({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [membersGroup, setMembersGroup] = useState<Group | null>(null);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: '',
    message: '',
  });

  const isActive = props.team.status === 'active' && !props.team.billingRequired;

  const syncSearchParams = (nextWeek: Dayjs, nextSub: SubTab) => {
    const params = new URLSearchParams();
    params.set('week', nextWeek.format('MM-DD-YYYY'));
    if (nextSub !== 'trainingPlan') {
      params.set('sub', nextSub);
    }
    setSearchParams(params);
  };

  const groupIdsKey = (props.team.groups || []).map((g) => String(g.id)).join(',');

  useEffect(() => {
    syncSearchParams(week, subTab);
  }, [week, subTab]);

  useEffect(() => {
    if (subTab === 'trainingPlan') {
      fetchAllTrainings();
    }
  }, [week, groupIdsKey, subTab]);

  const getWeekParams = (groupId?: string) => {
    const params: Record<string, string> = {
      start: addDays(startOfWeek(new Date(week.format('YYYY-MM-DD'))), 1).toISOString(),
      end: addDays(endOfWeek(new Date(week.format('YYYY-MM-DD'))), 1).toISOString(),
    };
    if (groupId) params.group = groupId;
    return new URLSearchParams(params).toString();
  };

  const fetchTeamTrainings = async (): Promise<any[]> => {
    setTeamLoading(true);
    try {
      const response = await trainingsService.getTrainingsByTeamId(props.team.id as string, getWeekParams());
      const trainings = normalizeTrainings(response.data.data || []);
      setTeamTrainings(trainings);
      setTeamWeekArr(buildWeekScheduleFromTrainings(week, trainings));
      setTeamStats(calculateSplitWeekStats(trainings));
      setTeamLoading(false);
      return trainings;
    } catch {
      setTeamLoading(false);
      showSnackbar('There was an error getting team trainings. Please try again later', 'error');
      return [];
    }
  };

  const fetchGroupTrainings = async (groupId: string, teamData: any[]) => {
    setGroupWeeks((prev) => ({
      ...prev,
      [groupId]: {
        ...(prev[groupId] || {
          groupId,
          trainings: [],
          weekSchedule: createEmptyWeekSchedule(week),
          stats: calculateSplitWeekStats([]),
        }),
        loading: true,
      },
    }));

    try {
      const response = await trainingsService.getTrainingsByTeamId(props.team.id as string, getWeekParams(groupId));
      const trainings = normalizeTrainings(response.data.data || []);
      const combined = [...trainings, ...teamData];
      setGroupWeeks((prev) => ({
        ...prev,
        [groupId]: {
          groupId,
          trainings,
          weekSchedule: buildWeekScheduleFromTrainings(week, trainings),
          stats: calculateSplitWeekStats(combined, groupId),
          loading: false,
        },
      }));
    } catch {
      setGroupWeeks((prev) => ({
        ...prev,
        [groupId]: {
          ...(prev[groupId] || {
            groupId,
            trainings: [],
            weekSchedule: createEmptyWeekSchedule(week),
            stats: calculateSplitWeekStats([]),
          }),
          loading: false,
        },
      }));
      showSnackbar('There was an error getting group trainings. Please try again later', 'error');
    }
  };

  const fetchAllTrainings = async () => {
    setModalState('');
    const teamData = await fetchTeamTrainings();
    const groups = props.team.groups || [];
    if (groups.length === 0) {
      setGroupWeeks({});
      setGroupsLoading(false);
      return;
    }
    setGroupsLoading(true);
    await Promise.all(groups.map((group) => fetchGroupTrainings(String(group.id), teamData)));
    setGroupsLoading(false);
  };

  const allUnpublishedIds = useMemo(() => {
    const ids = new Set<string>();
    teamStats.unpublishedIds.forEach((id) => ids.add(String(id)));
    Object.values(groupWeeks).forEach((gw) => {
      gw.trainings.filter((t) => !t.published).forEach((t) => ids.add(String(t.id)));
    });
    return Array.from(ids);
  }, [teamStats, groupWeeks]);

  const totalUnpublished = allUnpublishedIds.length;

  const selectedGroup = useMemo(() => {
    if (modalContext.type !== 'group' || !modalContext.groupId) return undefined;
    return props.team.groups.find((g) => String(g.id) === String(modalContext.groupId));
  }, [modalContext, props.team.groups]);

  const setContext = (type: 'team' | 'group', groupId?: string) => {
    setModalContext({ type, groupId });
  };

  const onRowEdit = (trainingId: string, type: 'team' | 'group', groupId?: string) => {
    setCurrentTraining(trainingId);
    setContext(type, groupId);
    setModalState('edit');
  };

  const onRowDelete = (trainingId: string) => {
    trainingsService
      .deleteTraining(props.team.id as string, trainingId)
      .then(() => {
        showSnackbar('Successfully deleted training', 'success');
        fetchAllTrainings();
      })
      .catch(() => {
        showSnackbar('There was an error while deleting this training. Please try again later', 'error');
      });
  };

  const onRowPublish = (trainingId: string, title?: string) => {
    trainingsService
      .publishTraining(props.team.id as string, trainingId)
      .then(() => {
        setPublishConfirm({
          open: true,
          title: 'Training Session Published!',
          message: title
            ? `"${title}" is now live. Athletes will be notified.`
            : 'This session is now live. Athletes will be notified.',
        });
        fetchAllTrainings();
      })
      .catch(() => {
        showSnackbar('There was an error while publishing this training. Please try again later', 'error');
      });
  };

  const onPublishIds = (trainingIds: string[], label: string) => {
    if (trainingIds.length === 0) return;
    trainingsService
      .publishAllTrainings(props.team.id as string, trainingIds)
      .then(() => {
        setPublishConfirm({
          open: true,
          title: 'Week Published!',
          message: `Unpublished sessions were published ${label}. Athletes will be notified.`,
        });
        fetchAllTrainings();
      })
      .catch(() => {
        showSnackbar('There was an error while publishing trainings. Please try again later', 'error');
      });
  };

  const onRowDuplicate = (trainingId: string, type: 'team' | 'group', groupId?: string) => {
    setCurrentTraining(trainingId);
    setContext(type, groupId);
    setModalState('duplicate');
  };

  const handleSubTabChange = (next: SubTab) => {
    setSubTab(next);
  };

  const handleWeekNav = (dir: number) => {
    if (!isActive) return;
    setWeek((prev) => prev.add(dir, 'week'));
  };

  const handleThisWeek = () => {
    if (!isActive) return;
    setWeek(getDefaultWeek());
  };

  const renderModal = () => {
    if (modalState === 'create') {
      return (
        <CreateTrainingDraftComponent
          open
          handleClose={() => setModalState('')}
          handleRefresh={() => fetchAllTrainings()}
          week={week}
          team={props.team}
          type="new"
          defaultDate={draftTrainingDate}
          group={modalContext.type === 'group' ? selectedGroup : undefined}
        />
      );
    }
    if (modalState === 'edit') {
      return (
        <EditTrainingComponent
          open
          handleClose={() => setModalState('')}
          handleRefresh={() => fetchAllTrainings()}
          week={week}
          team={props.team}
          trainingId={currentTraining}
        />
      );
    }
    if (modalState === 'duplicate') {
      return (
        <CreateTrainingDraftComponent
          open
          handleClose={() => setModalState('')}
          handleRefresh={() => fetchAllTrainings()}
          week={week}
          team={props.team}
          type="duplicate"
          trainingId={currentTraining}
          group={modalContext.type === 'group' ? selectedGroup : undefined}
        />
      );
    }
    if (trainingLibraryOpen) {
      return (
        <COETrainingLibraryComponent
          open={trainingLibraryOpen}
          handleClose={() => setTrainingLibraryOpen(false)}
          handleRefresh={() => fetchAllTrainings()}
          team={props.team}
          week={week}
          group={modalContext.type === 'group' ? selectedGroup : undefined}
          selectedDate={draftTrainingDate}
        />
      );
    }
    if (weeklyPlannerOpen) {
      return (
        <COEWeeklyPlannersComponent
          team={props.team}
          group={modalContext.type === 'group' ? selectedGroup : undefined}
          startDate={week.startOf('week').add(1, 'day').format()}
          open={weeklyPlannerOpen}
          onClose={() => setWeeklyPlannerOpen(false)}
          onRefresh={() => fetchAllTrainings()}
        />
      );
    }
    return null;
  };

  const findTrainingTitle = (trainingId: string): string | undefined => {
    const fromTeam = teamTrainings.find((t) => String(t.id) === String(trainingId));
    if (fromTeam) return fromTeam.title;
    for (const gw of Object.values(groupWeeks)) {
      const found = gw.trainings.find((t) => String(t.id) === String(trainingId));
      if (found) return found.title;
    }
    return undefined;
  };

  const renderTrainingPlan = () => (
    <>
      <div className="tp-week-nav">
        <IconButton size="small" onClick={() => handleWeekNav(-1)} disabled={!isActive} aria-label="Previous week">
          <ChevronLeftIcon />
        </IconButton>
        <span className="tp-week-nav__label">{getWeekDateRangeLabel(week)}</span>
        <IconButton size="small" onClick={() => handleWeekNav(1)} disabled={!isActive} aria-label="Next week">
          <ChevronRightIcon />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          onClick={handleThisWeek}
          disabled={!isActive}
          style={{ textTransform: 'none', borderRadius: 8 }}
        >
          Today
        </Button>
      </div>

      {totalUnpublished > 0 && (
        <div className="tp-unpub-banner">
          <div className="tp-unpub-banner__left">
            <WarningAmberOutlinedIcon style={{ color: '#D97706' }} />
            <div className="tp-unpub-banner__text">
              <strong>
                {totalUnpublished} unpublished training{totalUnpublished === 1 ? '' : 's'}
              </strong>
              {` across ${1 + (props.team.groups?.length || 0)} calendar${
                (props.team.groups?.length || 0) > 0 ? 's' : ''
              } this week…`}
            </div>
          </div>
          <Button
            variant="contained"
            onClick={() => onPublishIds(allUnpublishedIds, 'to the team')}
            disabled={!isActive}
            style={{
              backgroundColor: '#D97706',
              color: '#fff',
              textTransform: 'none',
              borderRadius: 8,
              boxShadow: 'none',
            }}
          >
            Publish All
          </Button>
        </div>
      )}

      {teamLoading ? (
        <>
          <COESkeletonComponent />
          <COESkeletonComponent />
        </>
      ) : (
        <TpWeekCalendar
          accent="team"
          title="Team Training Plan"
          subtitle="All Athletes"
          weekSchedule={teamWeekArr}
          stats={teamStats}
          disabled={!isActive}
          onEdit={(id) => onRowEdit(id, 'team')}
          onPublish={(id) => onRowPublish(id, findTrainingTitle(id))}
          onDuplicate={(id) => onRowDuplicate(id, 'team')}
          onDelete={onRowDelete}
          onPublishWeek={() => onPublishIds(teamStats.unpublishedIds, 'to the team')}
          onWeeklyPlans={() => {
            setContext('team');
            setWeeklyPlannerOpen(true);
          }}
          onAddNew={(date) => {
            setDraftTrainingDate(date);
            setContext('team');
            setModalState('create');
          }}
          onAddFromLibrary={(date) => {
            setDraftTrainingDate(date);
            setContext('team');
            setTrainingLibraryOpen(true);
          }}
        />
      )}

      {(props.team.groups || []).map((group) => {
        const groupId = String(group.id);
        const data = groupWeeks[groupId];
        if (groupsLoading && !data) {
          return <COESkeletonComponent key={groupId} />;
        }
        const stats = data?.stats || calculateSplitWeekStats([]);
        const weekSchedule = data?.weekSchedule || createEmptyWeekSchedule(week);

        return (
          <TpWeekCalendar
            key={groupId}
            accent="group"
            title={group.name}
            subtitle={`${group.members?.length || 0} athletes`}
            weekSchedule={weekSchedule}
            teamTrainingsByDay={teamWeekArr}
            groupId={groupId}
            group={group}
            stats={stats}
            loading={data?.loading}
            disabled={!isActive}
            onEdit={(id) => onRowEdit(id, 'group', groupId)}
            onPublish={(id) => onRowPublish(id, findTrainingTitle(id))}
            onDuplicate={(id) => onRowDuplicate(id, 'group', groupId)}
            onDelete={onRowDelete}
            onPublishWeek={() => onPublishIds(stats.unpublishedIds, `to ${group.name}`)}
            onWeeklyPlans={() => {
              setContext('group', groupId);
              setWeeklyPlannerOpen(true);
            }}
            onAddNew={(date) => {
              setDraftTrainingDate(date);
              setContext('group', groupId);
              setModalState('create');
            }}
            onAddFromLibrary={(date) => {
              setDraftTrainingDate(date);
              setContext('group', groupId);
              setTrainingLibraryOpen(true);
            }}
            onOpenMembers={() => setMembersGroup(group)}
          />
        );
      })}

      <button
        type="button"
        className="tp-create-group-card"
        disabled={!isActive}
        onClick={() => setCreateGroupOpen(true)}
      >
        <AddIcon />
        <Typography variant="subtitle1" style={{ fontWeight: 700, color: palette.purple.main }}>
          Create Group
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add a new training group calendar
        </Typography>
      </button>
    </>
  );

  return (
    <div className="team-details-page team-trainings-page">
      <div className="tp-sub-tabs">
        <button
          type="button"
          className={`tp-sub-tab${subTab === 'trainingPlan' ? ' tp-sub-tab--active' : ''}`}
          onClick={() => handleSubTabChange('trainingPlan')}
        >
          Training Plan
        </button>
        <button
          type="button"
          className={`tp-sub-tab${subTab === 'fieldsForms' ? ' tp-sub-tab--active' : ''}`}
          onClick={() => handleSubTabChange('fieldsForms')}
        >
          Fields & Forms
        </button>
      </div>

      <div className="tp-scroll-area">
        {subTab === 'trainingPlan' ? renderTrainingPlan() : <TpFieldsFormsPanel team={props.team} />}
      </div>

      {renderModal()}

      <TpGroupMembersDialog
        open={Boolean(membersGroup)}
        team={props.team}
        group={membersGroup}
        onClose={() => setMembersGroup(null)}
        onRefresh={() => props.refreshTeam()}
      />

      <TpCreateGroupDialog
        open={createGroupOpen}
        team={props.team}
        onClose={() => setCreateGroupOpen(false)}
        onCreated={() => props.refreshTeam()}
      />

      <Dialog
        open={publishConfirm.open}
        onClose={() => setPublishConfirm((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: 'center', paddingTop: 28 }}>
          <CheckCircleOutlineIcon
            style={{ fontSize: 48, color: palette.primary.main, display: 'block', margin: '0 auto 12px' }}
          />
          {publishConfirm.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
            {publishConfirm.message}
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: 20 }}>
          <Button variant="contained" onClick={() => setPublishConfirm((prev) => ({ ...prev, open: false }))}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamTrainingsPage;

export { createEmptyWeekSchedule } from '../../../../helpers/team-training-plan.helpers';
